import Vue from "vue";

/**
 * Makes every v-dialog in the app draggable by its own v-card-title header.
 * Registered once in main.js, so no change is needed in the individual modals.
 *
 * Fullscreen dialogs (the mobile layout) and touch/pen input are left alone:
 * there the modal fills the screen and dragging has no meaning.
 */

// only the header of the dialog's own top level card acts as a handle
const HANDLE = ".v-card__title";
// clicking these inside the header must keep working (close button, filters, ...)
const IGNORE =
  "button, a, input, textarea, select, [contenteditable], [role='button'], .v-input, .v-icon, .v-btn, .ps-closeModalBtn";
// how much of the dialog has to stay inside the viewport
const MIN_VISIBLE = 60;
// movement (px) before a press turns into a drag instead of a click
const THRESHOLD = 4;

let drag = null;

function position(el) {
  if (!el.__psDialogPos) el.__psDialogPos = { x: 0, y: 0 };
  return el.__psDialogPos;
}

function apply(el, x, y) {
  const pos = position(el);
  pos.x = x;
  pos.y = y;
  el.style.transform = x || y ? `translate(${x}px, ${y}px)` : "";
}

function reset(el) {
  if (el) apply(el, 0, 0);
}

// box is the dialog geometry with the current translate removed
function clamp(box, x, y) {
  const maxX = window.innerWidth - MIN_VISIBLE - box.left;
  const minX = MIN_VISIBLE - box.left - box.width;
  const maxY = window.innerHeight - MIN_VISIBLE - box.top;
  const minY = -box.top; // the header stays reachable at the top edge
  return [
    Math.min(Math.max(x, minX), maxX),
    Math.min(Math.max(y, minY), maxY),
  ];
}

function boxOf(el) {
  const rect = el.getBoundingClientRect();
  const pos = position(el);
  return {
    left: rect.left - pos.x,
    top: rect.top - pos.y,
    width: rect.width,
    height: rect.height,
  };
}

function onPointerDown(e) {
  if (drag || e.pointerType !== "mouse" || e.button !== 0) return;

  const handle = e.target.closest(HANDLE);
  if (!handle) return;

  const dialog = handle.closest(".v-dialog");
  if (!dialog || dialog.classList.contains("v-dialog--fullscreen")) return;

  // ignore headers of cards nested in the modal body
  const card = dialog.firstElementChild;
  if (!card || handle.parentElement !== card) return;

  if (e.target.closest(IGNORE)) return;

  const pos = position(dialog);
  drag = {
    dialog,
    box: boxOf(dialog),
    startX: e.clientX,
    startY: e.clientY,
    baseX: pos.x,
    baseY: pos.y,
    active: false,
  };

  window.addEventListener("pointermove", onPointerMove, true);
  window.addEventListener("pointerup", onPointerUp, true);
  window.addEventListener("pointercancel", onPointerUp, true);
}

function onPointerMove(e) {
  if (!drag) return;

  const dx = e.clientX - drag.startX;
  const dy = e.clientY - drag.startY;

  if (!drag.active) {
    if (Math.abs(dx) + Math.abs(dy) < THRESHOLD) return;
    drag.active = true;
    document.body.classList.add("ps-dialog-dragging");
  }

  e.preventDefault();
  const [x, y] = clamp(drag.box, drag.baseX + dx, drag.baseY + dy);
  apply(drag.dialog, x, y);
}

function onPointerUp() {
  window.removeEventListener("pointermove", onPointerMove, true);
  window.removeEventListener("pointerup", onPointerUp, true);
  window.removeEventListener("pointercancel", onPointerUp, true);

  if (drag && drag.active) {
    document.body.classList.remove("ps-dialog-dragging");
    // swallow the click that closes this drag, and disarm if none follows
    window.addEventListener("click", swallowClick, true);
    setTimeout(() => window.removeEventListener("click", swallowClick, true));
  }
  drag = null;
}

function swallowClick(e) {
  window.removeEventListener("click", swallowClick, true);
  e.stopPropagation();
  e.preventDefault();
}

function onResize() {
  document.querySelectorAll(".v-dialog").forEach((el) => {
    const pos = el.__psDialogPos;
    if (!pos || (!pos.x && !pos.y)) return;
    const [x, y] = clamp(boxOf(el), pos.x, pos.y);
    apply(el, x, y);
  });
}

export default {
  install() {
    document.addEventListener("pointerdown", onPointerDown, true);
    window.addEventListener("resize", onResize);

    // every modal opens centered again, wherever it was dragged last time
    Vue.mixin({
      mounted() {
        if (this.$options.name !== "v-dialog") return;
        this.$watch("isActive", (val) => {
          if (val) this.$nextTick(() => reset(this.$refs.dialog));
        });
      },
    });
  },
};
